import {
  ControlType,
  CustomFieldDefinition,
  CustomFieldUsage,
  FieldDataType,
  FieldStatus,
  FormatValidation,
  UserRole,
} from '@alucio/aws-beacon-amplify/src/models'
import { CustomFieldValuesMap, UserORM } from 'src/types/orms'
import { z } from 'zod'

const userCreatedAt =  new Date().toUTCString()

export type CustomFieldValues = {
  field: CustomFieldDefinition,
  values: string[],
  displayValues: string[],
}

export const fields: CustomFieldDefinition[] = [
  {
    id: 'givenName',
    fieldLabel: 'First name',
    fieldName: 'First name',
    status: FieldStatus.ENABLED,
    required: true,
    fieldType: FieldDataType.STRING,
    usage: [CustomFieldUsage.USER_FILTER],
    controlType: ControlType.INPUT,
    fieldValueDefinitions: [],
    createdAt: userCreatedAt,
  },
  {
    id: 'familyName',
    fieldLabel: 'Family Name',
    fieldName: 'Family Name',
    status: FieldStatus.ENABLED,
    required: true,
    fieldType: FieldDataType.STRING,
    usage: [CustomFieldUsage.USER_FILTER],
    controlType: ControlType.INPUT,
    fieldValueDefinitions: [],
    createdAt: userCreatedAt,
  },
  {
    id: 'role',
    fieldLabel: 'Role',
    fieldName: 'Role',
    status: FieldStatus.ENABLED,
    required: true,
    fieldType: FieldDataType.CATEGORICAL,
    usage: [CustomFieldUsage.USER_FILTER],
    controlType: ControlType.SELECT,
    fieldValueDefinitions: [{
      id: UserRole.TENANT_VIEWER,
      value: UserRole.TENANT_VIEWER,
      label: 'Viewer',
      disabled: false,
      createdAt: userCreatedAt,
      dependentValueIds: [],
    },
    {
      id: UserRole.TENANT_VIEW_ONLY,
      value: UserRole.TENANT_VIEW_ONLY,
      label: 'View Only',
      disabled: false,
      createdAt: userCreatedAt,
      dependentValueIds: [],
    },
    {
      id: UserRole.TENANT_PUBLISHER,
      value: UserRole.TENANT_PUBLISHER,
      label: 'Publisher',
      disabled: false,
      createdAt: userCreatedAt,
      dependentValueIds: [],
    }],
    createdAt: userCreatedAt,
  },
  {
    id: 'email',
    fieldLabel: 'Email',
    fieldName: 'Email',
    status: FieldStatus.ENABLED,
    required: true,
    fieldType: FieldDataType.STRING,
    usage: [CustomFieldUsage.USER_FILTER],
    controlType: ControlType.INPUT,
    formatValidation: FormatValidation.EMAIL,
    fieldValueDefinitions: [],
    createdAt: userCreatedAt,
  },
  {
    id: 'altUsername',
    fieldLabel: 'Altername username',
    fieldName: 'Altername username',
    status: FieldStatus.ENABLED,
    required: false,
    fieldType: FieldDataType.STRING,
    usage: [CustomFieldUsage.USER_FILTER],
    controlType: ControlType.INPUT,
    formatValidation: FormatValidation.EMAIL,
    fieldValueDefinitions: [],
    createdAt: userCreatedAt,
  },
  {
    id: 'isExcludedFromReporting',
    fieldLabel: 'Exclude from reporting',
    fieldName: 'Exclude from reporting',
    status: FieldStatus.ENABLED,
    fieldType: FieldDataType.CATEGORICAL,
    usage: [CustomFieldUsage.USER_FILTER],
    controlType: ControlType.CHECKBOX,
    fieldValueDefinitions: [
      {
        id: 'NO',
        value: 'NO',
        label: 'Exclude from reporting',
        createdAt: userCreatedAt,
        dependentValueIds: [],
      },
      {
        id: 'YES',
        value: 'YES',
        label: 'Exclude from reporting',
        createdAt: userCreatedAt,
        dependentValueIds: [],
      },
    ],
    createdAt: userCreatedAt,
  },
  { // if yes then add the role in the submit?
    id: 'TeamReportViewer',
    fieldLabel: 'Grant permissions to view Publisher reports',
    fieldName: 'TeamReportViewer',
    status: FieldStatus.ENABLED,
    fieldType: FieldDataType.CATEGORICAL,
    usage: [CustomFieldUsage.USER_FILTER],
    controlType: ControlType.CHECKBOX,
    fieldValueDefinitions: [
      {
        id: 'NO',
        value: 'NO', // if no its either tenant viewer or tenant publisher
        label: 'Grant permissions to view Publisher reports',
        createdAt: userCreatedAt,
        dependentValueIds: [],
      },
      {
        id: 'YES',
        value: 'YES', // not sure what value is here? or how it impacts the form but the value of the field is whatever the id is.
        label: 'Grant permissions to view Publisher reports',
        createdAt: userCreatedAt,
        dependentValueIds: [],
      },
    ],
    createdAt: userCreatedAt,
  },
  {
    id: 'TenantAdmin',
    fieldLabel: 'Grant administrative privileges',
    fieldName: 'TenantAdmin',
    status: FieldStatus.ENABLED,
    fieldType: FieldDataType.CATEGORICAL,
    usage: [CustomFieldUsage.USER_FILTER],
    controlType: ControlType.CHECKBOX,
    fieldValueDefinitions: [
      {
        id: 'NO',
        value: 'NO',
        label: 'Grant administrative privileges',
        createdAt: userCreatedAt,
        dependentValueIds: [],
      },
      {
        id: 'YES',
        value: 'YES',
        label: 'Grant administrative privileges',
        createdAt: userCreatedAt,
        dependentValueIds: [],
      },
    ],
    createdAt: userCreatedAt,
  },
  {
    id: 'LeadViewer',
    fieldLabel: 'Grant lead viewer permissions',
    fieldName: 'LeadViewer',
    status: FieldStatus.ENABLED,
    fieldType: FieldDataType.CATEGORICAL,
    usage: [CustomFieldUsage.USER_FILTER],
    controlType: ControlType.CHECKBOX,
    fieldValueDefinitions: [
      {
        id: 'NO',
        value: 'NO',
        label: 'Grant lead viewer permissions',
        createdAt: userCreatedAt,
        dependentValueIds: [],
      },
      {
        id: 'YES',
        value: 'YES',
        label: 'Grant lead viewer permissions',
        createdAt: userCreatedAt,
        dependentValueIds: [],
      },
    ],
    createdAt: userCreatedAt,
  },
]

const userEditEmailValidation = (value, ctx: z.RefinementCtx) => {
  if (value.altUsername === value.email) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Alternate email cannot be the same as email.',
      path: ['altUsername'],
    })
  }
}

const overrideSchema = {
  superRefine: [userEditEmailValidation],
}

export const userEditConfig = {
  fields,
  overrideSchema,
}

export const getUserEditDefaultValues = (userORM?: UserORM): CustomFieldValuesMap => {
  const givenName = userORM?.model.givenName || ''
  const familyName = userORM?.model.familyName || ''
  const role = userORM?.model.role || ''
  const email = userORM?.model.email || ''
  const altUsername = userORM?.model.altUsername || ''
  const isExcludedFromReporting = userORM?.model.isExcludedFromReporting

  return {
    givenName: {
      field: fields[0],
      values: [givenName],
      displayValues: [givenName],
    },
    familyName: {
      field: fields[1],
      values: [familyName],
      displayValues: [familyName],
    },
    role: {
      field: fields[2],
      values: [role],
      displayValues: [role],
    },
    email: {
      field: fields[3],
      values: [email],
      displayValues: [email],
    },
    altUsername: {
      field: fields[4],
      values: [altUsername],
      displayValues: [altUsername],
    },
    isExcludedFromReporting: {
      field: fields[5],
      // doing yes/no instead of true/false because the ComposableField expects the type of checkbox to be
      values: isExcludedFromReporting ? ['YES'] : ['NO'],
      displayValues: isExcludedFromReporting ? ['YES'] : ['NO'],
    },
    TeamReportViewer: {
      field: fields[6],
      // doing yes/no instead of true/false because the ComposableField expects the type of checkbox to be
      values: (userORM?.model.additionalRoles as UserRole[])?.includes(UserRole.TEAM_REPORT_VIEWER)
        ? ['YES']
        : ['NO'],
      displayValues: (userORM?.model.additionalRoles as UserRole[])?.includes(UserRole.TEAM_REPORT_VIEWER)
        ? ['YES']
        : ['NO'],
    },
    TenantAdmin: {
      field: fields[7],
      // doing yes/no instead of true/false because the ComposableField expects the type of checkbox to be
      values: (userORM?.model.additionalRoles as UserRole[])?.includes(UserRole.TENANT_ADMIN) ? ['YES'] : ['NO'],
      displayValues: (userORM?.model.additionalRoles as UserRole[])?.includes(UserRole.TENANT_ADMIN) ? ['YES'] : ['NO'],
    },
    LeadViewer: {
      field: fields[8],
      values: userORM?.model.additionalRoles?.some((role) => role === UserRole.LEAD_VIEWER) ? ['YES'] : ['NO'],
      displayValues: userORM?.model.additionalRoles?.some((role) => role === UserRole.LEAD_VIEWER) ? ['YES'] : ['NO'],
    },
  }
}

export const getUserEditCustomFieldValues = (userORM?: UserORM, customFields?: CustomFieldDefinition[]) => {
  const userEditCustomFields = customFields?.reduce<Record<string, CustomFieldValues>>((acc, field) => {
    acc[field.id] = {
      field: field,
      values: userORM?.meta.customFilterValues[field.id]?.valuesDefinition?.map(({ id }) => id),
      displayValues: userORM?.meta.customFilterValues[field.id]?.valuesDefinition?.map(({ value }) => value),
    }
    return acc;
  }, {});
  return userEditCustomFields
}
